import { Box } from "@mui/material";
import Button from "components/common/Button";
import InputField from "components/common/FormComponents/InputField";
import CustomModal from "components/common/Modal/CustomModal";
import Text from "components/common/Typography/Text";
import { useFormik } from "formik";
import * as toast from "hooks/notify";
import { useFeedBackMutation } from "hooks/useFeedback";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { reportAnIssueSchema } from "./index.schema";

interface ReportAnIssueModalProps {
  isOpen: boolean;
  onClose: (value: boolean) => void;
  itemforFeedback?: string;
  title?: string;
  showTextAreaLabel?: boolean;
  mainDescription?: string;
  encounterId?: string | number | null;
}

interface FeedbackFormValues {
  feedback: string;
}

interface RootState {
  auth: {
    userId: string | number;
  };
  globalStates: {
    isRecording?: boolean;
  };
}

const ReportAnIssueModal = ({
  isOpen,
  onClose,
  itemforFeedback = "generic",
  title = "Report An Issue",
  showTextAreaLabel = true,
  mainDescription = "We appreciate your feedback on our Saya AI-generated information. Leave a comment below report any issues.",
  encounterId = null,
}: ReportAnIssueModalProps) => {
  const { patientId } = useParams<{ patientId: string }>();
  const { mutationFeedback } = useFeedBackMutation();
  const { userId } = useSelector((state: RootState) => state.auth);
  const isEncounterRecording = useSelector((state: RootState) => state.globalStates.isRecording);

  // Track previous open state to detect when modal closes
  const prevIsOpenRef = useRef(isOpen);

  const handleCloseReportIssue = () => {
    onClose(false);
  };

  const formik = useFormik<FeedbackFormValues>({
    initialValues: {
      feedback: "",
    },
    validationSchema: reportAnIssueSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  // Store resetForm in ref to avoid dependency on formik object
  const resetFormRef = useRef(formik.resetForm);
  resetFormRef.current = formik.resetForm;

  // Cleanup: Reset form when modal closes
  // This ensures if user was recording and closed modal without stopping,
  // the next time they open the modal, it starts fresh
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      // Modal just closed
      resetFormRef.current();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  const handleSubmit = async (data: FeedbackFormValues) => {
    try {
      await mutationFeedback.mutateAsync({
        ...data,
        user_id: userId,
        item_for_feedback: itemforFeedback,
        item_id: encounterId || patientId || "",
      });
      toast.success("Thank you for your feedback");
      formik.resetForm();
      handleCloseReportIssue();
    } catch {
      // Error handling is done by the mutation hook's onError callback
      // Modal stays open so user can retry
    }
  };

  return (
    <CustomModal
      setOpen={handleCloseReportIssue}
      open={isOpen}
      title={title}
      maxWidth="sm"
      sxDialogContent={{
        padding: "32px 40px 24px 40px !important",
      }}>
      <Box>
        <Box
          sx={{
            width: "481px",
          }}>
          <Box
            sx={{
              mb: 2,
            }}>
            <Text variant="bodyS" sx={{ whiteSpace: "normal" }}>
              {mainDescription}
            </Text>
          </Box>
          {showTextAreaLabel && (
            <Text variant="bodyS" fontWeight={500}>
              Describe The Issue
            </Text>
          )}

          <InputField
            name="feedback"
            placeholder="Enter Comment"
            rows={4}
            cols={12}
            multiline
            speechEnabled={true}
            speechDisabledReason={isEncounterRecording ? "Microphone is in use by the visit recording" : undefined}
            formik={formik}
            sxInputProps={{ fontSize: "14px" }}
            data-testid="input-feedback"
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 6,
            }}>
            <Button variant="outlined" id="cancel-button" text="Cancel" onClick={handleCloseReportIssue} />
            <Button
              id="submit-button"
              disabled={mutationFeedback.isPending}
              text="Submit"
              onClick={formik.handleSubmit}
              data-testid="submit-button"
            />
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ReportAnIssueModal;
